import { useEffect, useRef, useState } from 'react';
import { Icon } from '@ab-inbev-maz/compass';
import type { IconName } from '@ab-inbev-maz/compass/dist/entities/components/icon.js';

export interface ContextMenuItem {
  id: string;
  label: string;
  icon?: IconName;
  iconPosition?: 'left' | 'right'; // defaults to 'left'
  hint?: string; // optional secondary text, shown to the right of the label
  checked?: boolean;
  danger?: boolean;
  divider?: boolean; // draws a separator above this item
  keepOpen?: boolean; // doesn't close the menu when selected (useful for toggles)
  submenu?: ContextMenuItem[]; // if present, the item only opens the submenu on hover
  onSelect: () => void;
}

interface ContextMenuProps {
  x: number;
  y: number;
  items: ContextMenuItem[];
  onClose: () => void;
}

const MENU_WIDTH = 180;
const ITEM_HEIGHT = 36;
const SUBMENU_CLOSE_DELAY_MS = 150;

interface MenuItemRowProps {
  item: ContextMenuItem;
  isHighlighted: boolean;
  onClick: () => void;
  onMouseEnter?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseLeave?: () => void;
}

const MenuItemRow = ({ item, isHighlighted, onClick, onMouseEnter, onMouseLeave }: MenuItemRowProps) => (
  <div>
    {item.divider && <div className="my-1 border-t border-gray-200" />}
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`w-full flex items-center gap-1 px-2 py-1 text-sm text-left cursor-pointer ${
        item.danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-100'
      } ${isHighlighted ? 'bg-gray-100' : ''}`}
    >
      {item.icon && item.iconPosition !== 'right' && (
        <span className="w-4"> <Icon name={item.icon} /> </span>
      )}
      <span className="flex-1">{item.label}</span>
      {item.icon && item.iconPosition === 'right' && (
        <span className="w-4"> <Icon name={item.icon} /> </span>
      )}
      {item.hint && <span className="text-xs text-gray-400">{item.hint}</span>}
      {item.checked && (
        <span className="text-[#202020]">
          <Icon name="validation/checkmark-2" />
        </span>
      )}
    </button>
  </div>
);

// Generic context menu: any canvas feature builds its own item list,
// without having to touch this component. Same style as the user dropdown in Header.tsx.
const ContextMenu = ({ x, y, items, onClose }: ContextMenuProps) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [submenu, setSubmenu] = useState<{ item: ContextMenuItem; top: number; left: number } | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handlePointerDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) onClose();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  useEffect(
    () => () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    },
    []
  );

  const clampedX = Math.max(8, Math.min(x, window.innerWidth - MENU_WIDTH - 8));
  const clampedY = Math.max(8, Math.min(y, window.innerHeight - items.length * ITEM_HEIGHT - 8));

  const cancelSubmenuClose = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const scheduleSubmenuClose = () => {
    cancelSubmenuClose();
    closeTimeoutRef.current = setTimeout(() => setSubmenu(null), SUBMENU_CLOSE_DELAY_MS);
  };

  // Hovering an item with a submenu opens it beside it (or on the left side if it doesn't
  // fit on the right); hovering any other item closes the submenu immediately.
  const handleItemMouseEnter = (item: ContextMenuItem, e: React.MouseEvent<HTMLButtonElement>) => {
    if (!item.submenu) {
      cancelSubmenuClose();
      setSubmenu(null);
      return;
    }
    cancelSubmenuClose();
    const rect = e.currentTarget.getBoundingClientRect();
    const submenuHeight = item.submenu.length * ITEM_HEIGHT;
    const overflowsRight = rect.right + 4 + MENU_WIDTH > window.innerWidth - 8;
    const left = overflowsRight ? rect.left - MENU_WIDTH - 4 : rect.right + 4;
    const top = Math.max(8, Math.min(rect.top, window.innerHeight - submenuHeight - 8));
    setSubmenu({ item, top, left });
  };

  return (
    <div
      ref={menuRef}
      style={{ top: clampedY, left: clampedX, width: MENU_WIDTH }}
      className="fixed z-50 py-1 bg-white rounded-md shadow-xl border border-gray-200"
    >
      {items.map((item) => (
        <MenuItemRow
          key={item.id}
          item={item}
          isHighlighted={submenu?.item.id === item.id}
          onClick={() => {
            if (item.submenu) return; // only opens the submenu, runs no action
            item.onSelect();
            if (!item.keepOpen) onClose();
          }}
          onMouseEnter={(e) => handleItemMouseEnter(item, e)}
          onMouseLeave={() => {
            if (item.submenu) scheduleSubmenuClose();
          }}
        />
      ))}

      {submenu && (
        <div
          style={{ top: submenu.top, left: submenu.left, width: MENU_WIDTH }}
          onMouseEnter={cancelSubmenuClose}
          onMouseLeave={scheduleSubmenuClose}
          className="fixed z-50 py-1 bg-white rounded-md shadow-xl border border-gray-200"
        >
          {submenu.item.submenu?.map((subItem) => (
            <MenuItemRow
              key={subItem.id}
              item={subItem}
              isHighlighted={false}
              onClick={() => {
                subItem.onSelect();
                if (!subItem.keepOpen) onClose();
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ContextMenu;
